import React, { useState, useMemo, useEffect } from 'react';
import { useTableData } from './useTableData';
import { useTableState } from './useTableState';
import { OmniSearch } from './OmniSearch';
import { TableHeaders } from './TableHeaders';
import { TableBody } from './TableBody';
import { SearchableSelect } from './SearchableSelect';
import { ColumnInjector } from './ColumnInjector';
import { GroupByControls } from './GroupByControls';

import { AddRecordModal } from './AddRecordModal';

export function DynamicTableViewer({ targetTable, token, uiConfig, onMissingConfig, apiPrefix = '/api/data', baseFilters, viewKey, forceHideHeaders = false, selectionMode = false, onSelectRecord, onTableChange }) {
    const tableState = useTableState({ targetTable, token, viewKey });
    const tableData = useTableData({ 
        targetTable, token, apiPrefix, uiConfig, onMissingConfig, 
        injectedColumns: tableState.injectedColumns, 
        preferencesLoaded: tableState.preferencesLoaded,
        distinctMode: tableState.distinctMode,
        hiddenColumns: tableState.hiddenColumns,
        activeSubgrids: tableState.activeSubgrids,
        searchQuery: tableState.searchQuery,
        limit: tableState.limit,
        offset: tableState.offset,
        baseFilters: baseFilters
    });

    const [isAdding, setIsAdding] = useState(false);
    const [showTopControls, setShowTopControls] = useState(!viewKey || !viewKey.startsWith('subgrid_'));
    const [editingRowPk, setEditingRowPk] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [deletingRowPk, setDeletingRowPk] = useState(null);

    const isHeadersHidden = tableState.headerVisibility === 'hide' || (tableState.headerVisibility === 'auto' && forceHideHeaders);
    const passCascadeHideHeaders = tableState.cascadeHideHeaders || forceHideHeaders;

    // Data Processing Pipeline
    const processedData = useMemo(() => {
        let result = [...tableData.data];

        // 1. Filter
        if (tableState.searchQuery) {
            const query = tableState.searchQuery.trim().toLowerCase();
            const colMatch = query.match(/^@(\w+):\s*(.*)$/i);
            
            if (colMatch) {
                // Targeted column search
                const colName = colMatch[1];
                const searchVal = colMatch[2].toLowerCase();
                
                result = result.filter(row => {
                    let val = row[colName];
                    if (val === null || val === undefined) return false;
                    
                    // If it's an FK, search against the resolved label too
                    if (tableData.fkOptions[colName]) {
                        const match = tableData.fkOptions[colName].options.find(opt => opt.value === val);
                        if (match && String(match.label).toLowerCase().includes(searchVal)) return true;
                    }
                    return String(val).toLowerCase().includes(searchVal);
                });
            } else if (!query.startsWith('@')) {
                // General search
                result = result.filter(row => {
                    return Object.entries(row).some(([colName, val]) => {
                        if (val === null || val === undefined) return false;
                        if (tableData.fkOptions[colName]) {
                            const match = tableData.fkOptions[colName].options.find(opt => opt.value === val);
                            if (match && String(match.label).toLowerCase().includes(query)) return true;
                        }
                        return String(val).toLowerCase().includes(query);
                    });
                });
            }
        }

        // 2. Sort
        if (tableState.sortConfig.length > 0) {
            result.sort((a, b) => {
                for (const sort of tableState.sortConfig) {
                    let valA = a[sort.key];
                    let valB = b[sort.key];
                    
                    // Use resolved labels for FKs if available
                    if (tableData.fkOptions[sort.key]) {
                        const matchA = tableData.fkOptions[sort.key].options.find(opt => opt.value === valA);
                        if (matchA) valA = matchA.label;
                        const matchB = tableData.fkOptions[sort.key].options.find(opt => opt.value === valB);
                        if (matchB) valB = matchB.label;
                    }

                    if (valA === valB) continue;
                    if (valA === null || valA === undefined) return 1;
                    if (valB === null || valB === undefined) return -1;
                    
                    const aStr = String(valA).toLowerCase();
                    const bStr = String(valB).toLowerCase();
                    const compare = aStr < bStr ? -1 : 1;
                    
                    return sort.direction === 'asc' ? compare : -compare;
                }
                return 0;
            });
        }

        return result;
    }, [tableData.data, tableData.fkOptions, tableState.searchQuery, tableState.sortConfig]);

    // Grouping computation
    const effectiveGroupByConfig = useMemo(() => {
        let expanded = [];
        tableState.groupByConfig.forEach(col => {
            if (col.endsWith('->[Hierarchy]')) {
                let maxDepth = 1;
                processedData.forEach(row => {
                    const val = row[col];
                    if (val && typeof val === 'string') {
                        const parts = val.split(' \\ ');
                        if (parts.length > maxDepth) maxDepth = parts.length;
                    }
                });
                for (let i = 0; i < maxDepth; i++) {
                    expanded.push(`${col}::${i}`);
                }
            } else {
                expanded.push(col);
            }
        });
        return expanded;
    }, [tableState.groupByConfig, processedData]);

    const groups = useMemo(() => {
        if (effectiveGroupByConfig.length === 0) return null;
        
        const currentKeyFull = effectiveGroupByConfig[0];
        const [currentKey, depthStr] = currentKeyFull.split('::');
        const hierarchyDepth = depthStr ? parseInt(depthStr, 10) : null;
        
        const grouped = {};
        const standaloneRows = [];
        
        processedData.forEach(row => {
            let groupVal = row[currentKey];
            
            if (tableData.isPolymorphicTable && currentKey === 'TargetRecordID') {
                const polyKey = `${row.TargetSchema}.${row.TargetTable}.${row.TargetRecordID}`;
                if (tableData.polymorphicLabels && tableData.polymorphicLabels[polyKey]) {
                    groupVal = tableData.polymorphicLabels[polyKey];
                }
            }
            
            if (hierarchyDepth !== null && typeof groupVal === 'string') {
                const parts = groupVal.split(' \\ ');
                if (hierarchyDepth < parts.length) {
                    groupVal = parts[hierarchyDepth];
                } else {
                    standaloneRows.push(row);
                    return;
                }
            }
            
            const keyStr = String(groupVal ?? 'null');
            if (!grouped[keyStr]) grouped[keyStr] = [];
            grouped[keyStr].push(row);
        });
        return { groupCol: currentKeyFull, chunks: grouped, standaloneRows };
    }, [processedData, effectiveGroupByConfig, tableData.isPolymorphicTable, tableData.polymorphicLabels]);

    const visibleColumns = useMemo(() => {
        if (!tableData.columns) return [];
        let cols = tableData.columns.filter(c => !(tableState.hiddenColumns || []).includes(c.ColumnName));
        if (tableState.columnOrder && tableState.columnOrder.length > 0) {
            cols.sort((a, b) => {
                const idxA = tableState.columnOrder.indexOf(a.ColumnName);
                const idxB = tableState.columnOrder.indexOf(b.ColumnName);
                if (idxA === -1 && idxB === -1) return 0;
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
                return idxA - idxB;
            });
        }
        return cols;
    }, [tableData.columns, tableState.hiddenColumns, tableState.columnOrder]);


    // Handlers
    const handleAddClick = () => {
        if (tableData.schemaWarning) return alert(tableData.schemaWarning);
        setIsAdding(true);
        setEditingRowPk(null);
        setEditFormData({});
    };

    const handleEditClick = (row) => {
        if (tableData.schemaWarning) return alert(tableData.schemaWarning);
        const pkCols = tableData.getPrimaryKeyColumns();
        const pkMap = {};
        pkCols.forEach(c => { pkMap[c.ColumnName] = row[c.ColumnName]; });
        setIsAdding(false);
        setEditingRowPk(JSON.stringify(pkMap));
        setEditFormData({ ...row });
    };

    const handleInputChange = (e, colName) => {
        const val = e.target.value;
        setEditFormData(prev => {
            const updated = { ...prev, [colName]: val };
            if (tableData.isPolymorphicTable) {
                if (colName === 'TargetSchema') {
                    updated.TargetTable = '';
                    updated.TargetRecordID = '';
                } else if (colName === 'TargetTable') {
                    updated.TargetRecordID = '';
                }
            }
            return updated;
        });
    };

    const handleSaveClick = async (isUpdateCheck) => {
        const pkCols = tableData.getPrimaryKeyColumns();
        const isUpdate = !isAdding;
        const payload = {};
        const pkMap = {};

        if (isUpdate) {
            pkCols.forEach(c => { pkMap[c.ColumnName] = editFormData[c.ColumnName]; });
        }

        tableData.columns.forEach(col => {
            if (col.IsIdentity || (isUpdate && col.IsPrimaryKey)) return;
            if (editFormData[col.ColumnName] !== undefined) {
                payload[col.ColumnName] = editFormData[col.ColumnName];
            }
        });

        const success = await tableData.saveRow(isUpdate, pkMap, payload);
        if (success) {
            setIsAdding(false);
            setEditingRowPk(null);
            setEditFormData({});
        }
    };

    const handleDeleteClick = async (row) => {
        if (tableData.schemaWarning) return alert(tableData.schemaWarning);
        const pkCols = tableData.getPrimaryKeyColumns();
        const pkMap = {};
        pkCols.forEach(c => { pkMap[c.ColumnName] = row[c.ColumnName]; });
        setDeletingRowPk(JSON.stringify(pkMap));
        await tableData.checkDependencies(pkMap);
    };

    const confirmDelete = async () => {
        const pkMap = JSON.parse(deletingRowPk);
        const success = await tableData.deleteRow(pkMap);
        if (success) {
            setDeletingRowPk(null);
        }
    };

    const [loadingPolyRecords, setLoadingPolyRecords] = useState(false);
    const [polymorphicRecords, setPolymorphicRecords] = useState([]);

    // Fetch polymorphic target records when schema and table are selected
    useEffect(() => {
        let isMounted = true;
        
        const fetchPolyRecords = async () => {
            const { TargetSchema, TargetTable } = editFormData;
            if (!TargetSchema || !TargetTable || !tableData.isPolymorphicTable) {
                setPolymorphicRecords([]);
                return;
            }

            setLoadingPolyRecords(true);
            try {
                const headers = { 'Authorization': `Bearer ${token}` };
                
                const metaRes = await fetch(`${apiPrefix}/schema/metadata/${TargetSchema}/${TargetTable}`, { headers });
                let pkCol = 'id';
                if (metaRes.ok) {
                    const meta = await metaRes.json();
                    const pkMeta = meta.find(m => m.IsPrimaryKey);
                    if (pkMeta) pkCol = pkMeta.ColumnName;
                }

                const targetUiConf = Array.isArray(uiConfig) ? uiConfig.find(c => c.TargetSchema === TargetSchema && c.TargetTable === TargetTable) : null;
                const displayCol = targetUiConf ? targetUiConf.DisplayColumn : null;

                const res = await fetch(`${apiPrefix}/${TargetSchema}/${TargetTable}`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    const options = data.map(row => ({
                        value: row[pkCol],
                        label: displayCol && row[displayCol] ? row[displayCol] : String(row[pkCol])
                    }));
                    if (isMounted) setPolymorphicRecords(options);
                }
            } catch (err) {
                console.error("Failed to fetch polymorphic records:", err);
            } finally {
                if (isMounted) setLoadingPolyRecords(false);
            }
        };

        fetchPolyRecords();

        return () => { isMounted = false; };
    }, [editFormData.TargetSchema, editFormData.TargetTable, tableData.isPolymorphicTable, apiPrefix, token, uiConfig]);

    const uniqueSchemas = Array.from(new Set(tableData.tablesList.map(t => t.split('.')[0])));

    if (!targetTable) return <div className="p-4 text-slate-400">Please select a table from the dropdown to view its data.</div>;
    if (!tableState.preferencesLoaded) return <div className="p-4 text-slate-400">Loading preferences...</div>;
    if (tableData.loading && tableData.data.length === 0) return <div className="p-4 text-slate-400">Loading table data...</div>;
    if (tableData.error) return <div className="p-4 text-red-400">Error: {tableData.error}</div>;

    return (
        <div className="flex flex-col h-full relative">
            {tableData.schemaWarning && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 m-4 rounded">
                    {tableData.schemaWarning}
                </div>
            )}

            {showTopControls && (
                <div className="p-4 bg-slate-800 border-b border-slate-700 flex flex-col gap-4 shrink-0 shadow-md z-20 relative">
                    <button 
                        onClick={() => setShowTopControls(false)}
                        className="absolute top-2 right-2 text-slate-500 hover:text-red-400 bg-slate-900/50 hover:bg-slate-900 p-1 rounded-full transition-colors"
                        title="Hide Controls"
                    >
                        <span className="material-symbols-outlined text-[16px]">close_fullscreen</span>
                    </button>
                    <div className="flex justify-between items-center pr-8">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                {onTableChange && tableData.tablesList && tableData.tablesList.length > 0 ? (
                                    <div className="w-[300px]">
                                        <SearchableSelect
                                            value={targetTable}
                                            onChange={e => onTableChange(e.target.value)}
                                            options={tableData.tablesList.map(t => ({ value: t, label: t }))}
                                            className="bg-transparent text-lg font-medium text-blue-300 outline-none cursor-pointer border-b border-transparent hover:border-blue-400/50 pb-0.5 transition-colors"
                                            isClearable={false}
                                        />
                                    </div>
                                ) : (
                                    <h3 className="text-lg font-medium text-white">{targetTable}</h3>
                                )}
                                <div className="group/sql relative">
                                    <button className="text-slate-500 hover:text-blue-400 cursor-help transition-colors">
                                        <span className="material-symbols-outlined text-[18px]">data_object</span>
                                    </button>
                                    <div className="absolute left-0 top-full mt-1 w-[600px] max-h-[400px] overflow-auto bg-slate-900 border border-slate-700 text-teal-300 font-mono text-[10px] p-3 rounded shadow-2xl opacity-0 group-hover/sql:opacity-100 pointer-events-none group-hover/sql:pointer-events-auto transition-opacity z-50 whitespace-pre-wrap">
                                        {tableData.lastQuery || 'No query available'}
                                    </div>
                                </div>
                            </div>
                            {/* Column Injector restored */}
                            <div className="mt-1">
                                <ColumnInjector 
                                    fks={tableData.fks}
                                    reverseFks={tableData.reverseFks}
                                    isPolymorphicTable={tableData.isPolymorphicTable}
                                    showPolymorphicInjector={false}
                                    injectedColumns={tableState.injectedColumns}
                                    toggleInjectedColumn={tableState.toggleInjectedColumn}
                                    activeSubgrids={tableState.activeSubgrids}
                                    setActiveSubgrids={tableState.setActiveSubgrids}
                                    token={token}
                                    apiPrefix={apiPrefix}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4 items-center flex-1 ml-8 mr-4">
                            <GroupByControls tableState={tableState} />
                            <OmniSearch 
                                searchQuery={tableState.searchQuery}
                                setSearchQuery={tableState.setSearchQuery}
                                columns={tableData.columns}
                                fkOptions={tableData.fkOptions}
                            />
                        </div>
                        <button 
                            onClick={handleAddClick}
                            disabled={!!tableData.schemaWarning || isAdding || editingRowPk}
                            className="bg-green-600/20 hover:bg-green-500/30 text-green-400 disabled:opacity-50 disabled:cursor-not-allowed border border-green-500/30 rounded px-2 py-1 flex items-center justify-center transition-colors shadow-sm"
                            title="Add Record"
                        >
                            <span className="material-symbols-outlined text-[18px]">add</span>
                        </button>
                    </div>
                </div>
            )}
            
            {!showTopControls && (
                <div className="bg-slate-800 border-b border-slate-700 px-4 py-1.5 flex items-center gap-2 shrink-0 z-10 relative">
                    <span className="text-sm font-medium text-blue-300">{targetTable}</span>
                    {tableState.searchQuery && (
                        <span className="text-xs text-teal-400 italic bg-teal-900/30 px-2 py-0.5 rounded border border-teal-800 ml-2">
                            Search active: {tableState.searchQuery}
                        </span>
                    )}
                    <button 
                        onClick={() => setShowTopControls(true)}
                        className="absolute top-1.5 right-2 text-slate-500 hover:text-blue-400 bg-slate-900/50 hover:bg-slate-900 p-1 rounded-full transition-colors flex items-center justify-center"
                        title="Show Table Controls"
                    >
                        <span className="material-symbols-outlined text-[16px]">open_in_full</span>
                    </button>
                </div>
            )}

            <div className="overflow-x-auto flex-1 bg-slate-800">
                {groups ? (
                    <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 custom-scrollbar flex flex-col gap-2">
                        {Object.entries(groups.chunks).map(([groupVal, rows]) => (
                            <CollapsibleGroup
                                key={groupVal}
                                groupValue={groupVal}
                                groupColumn={groups.groupCol}
                                rows={rows}
                                depth={0}
                                effectiveGroupByConfig={effectiveGroupByConfig}
                                tableData={tableData}
                                tableState={tableState}
                                visibleColumns={visibleColumns}
                                targetTable={targetTable}
                                token={token}
                                uiConfig={uiConfig}
                                apiPrefix={apiPrefix}
                                selectionMode={selectionMode}
                                onSelectRecord={onSelectRecord}
                                isHeadersHidden={isHeadersHidden}
                                passCascadeHideHeaders={passCascadeHideHeaders}
                                editingRowPk={editingRowPk}
                                handleEditClick={handleEditClick}
                                handleDeleteClick={handleDeleteClick}
                                handleSaveClick={handleSaveClick}
                                handleCancelClick={() => { setIsAdding(false); setEditingRowPk(null); setEditFormData({}); }}
                                isAdding={isAdding}
                                editFormData={editFormData}
                                handleInputChange={handleInputChange}
                                uniqueSchemas={uniqueSchemas}
                                loadingPolyRecords={loadingPolyRecords}
                                polymorphicRecords={polymorphicRecords}
                            />
                        ))}
                        {groups.standaloneRows && groups.standaloneRows.length > 0 && (
                            <div className="mt-4 border-t border-slate-700 pt-4">
                                <table className="w-full text-left text-sm text-slate-300">
                                    <TableHeaders 
                                        allColumns={tableData.columns}
                                        columns={visibleColumns}
                                        fks={tableData.fks}
                                        fkOptions={tableData.fkOptions}
                                        sortConfig={tableState.sortConfig}
                                        handleSort={tableState.handleSort}
                                        isHeadersHidden={isHeadersHidden}
                                        headerVisibility={tableState.headerVisibility}
                                        setHeaderVisibility={tableState.setHeaderVisibility}
                                        cascadeHideHeaders={tableState.cascadeHideHeaders}
                                        setCascadeHideHeaders={tableState.setCascadeHideHeaders}
                                        columnWidths={tableState.columnWidths}
                                        setColumnWidths={tableState.setColumnWidths}
                                        customColumnNames={tableState.customColumnNames}
                                        setCustomColumnNames={tableState.setCustomColumnNames}
                                        columnFilters={tableState.columnFilters}
                                        setColumnFilters={tableState.setColumnFilters}
                                        setHiddenColumns={tableState.setHiddenColumns}
                                        toggleInjectedColumn={tableState.toggleInjectedColumn}
                                        draggingCol={tableState.draggingCol}
                                        setDraggingCol={tableState.setDraggingCol}
                                        dragOverCol={tableState.dragOverCol}
                                        setDragOverCol={tableState.setDragOverCol}
                                        addSelfSubgrid={() => {
                                            const sg = `${targetTable}:SELF`;
                                            tableState.setActiveSubgrids(prev => prev.includes(sg) ? prev : [...prev, sg]);
                                        }}
                                    />
                                    <TableBody 
                                        parentTable={targetTable}
                                        processedData={groups ? groups.standaloneRows : []}
                                        allColumns={tableData.columns}
                                        columns={visibleColumns}
                                        pkCols={tableData.getPrimaryKeyColumns()}
                                        fks={tableData.fks}
                                        reverseFks={tableData.reverseFks}
                                        fkOptions={tableData.fkOptions}
                                        isPolymorphicTable={tableData.isPolymorphicTable}
                                        polymorphicLabels={tableData.polymorphicLabels}
                                        editingRowPk={editingRowPk}
                                        handleEditClick={handleEditClick}
                                        handleDeleteClick={handleDeleteClick}
                                        handleSaveClick={handleSaveClick}
                                        handleCancelClick={() => { setIsAdding(false); setEditingRowPk(null); setEditFormData({}); }}
                                        schemaWarning={tableData.schemaWarning}
                                        isAdding={isAdding}
                                        editFormData={editFormData}
                                        handleInputChange={handleInputChange}
                                        uniqueSchemas={uniqueSchemas}
                                        tablesList={tableData.tablesList}
                                        loadingPolyRecords={loadingPolyRecords}
                                        polymorphicRecords={polymorphicRecords}
                                        distinctMode={tableState.distinctMode}
                                        selectionMode={selectionMode}
                                        onSelectRecord={onSelectRecord}
                                        SubgridComponent={DynamicTableViewer}
                                        forceHideHeaders={passCascadeHideHeaders}
                                        activeSubgrids={tableState.activeSubgrids}
                                        token={token}
                                        uiConfig={uiConfig}
                                        apiPrefix={apiPrefix}
                                        draggingCol={tableState.draggingCol}
                                        dragOverCol={tableState.dragOverCol}
                                    />
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <table className="w-full text-left text-sm text-slate-300">
                        <TableHeaders 
                            allColumns={tableData.columns}
                            columns={visibleColumns}
                            fks={tableData.fks}
                            fkOptions={tableData.fkOptions}
                            sortConfig={tableState.sortConfig}
                            handleSort={tableState.handleSort}
                            isHeadersHidden={isHeadersHidden}
                            headerVisibility={tableState.headerVisibility}
                            setHeaderVisibility={tableState.setHeaderVisibility}
                            cascadeHideHeaders={tableState.cascadeHideHeaders}
                            setCascadeHideHeaders={tableState.setCascadeHideHeaders}
                            columnWidths={tableState.columnWidths}
                            setColumnWidths={tableState.setColumnWidths}
                            customColumnNames={tableState.customColumnNames}
                            setCustomColumnNames={tableState.setCustomColumnNames}
                            columnFilters={tableState.columnFilters}
                            setColumnFilters={tableState.setColumnFilters}
                            setHiddenColumns={tableState.setHiddenColumns}
                            toggleInjectedColumn={tableState.toggleInjectedColumn}
                            draggingCol={tableState.draggingCol}
                            setDraggingCol={tableState.setDraggingCol}
                            dragOverCol={tableState.dragOverCol}
                            setDragOverCol={tableState.setDragOverCol}
                            addSelfSubgrid={() => {
                                const sg = `${targetTable}:SELF`;
                                tableState.setActiveSubgrids(prev => prev.includes(sg) ? prev : [...prev, sg]);
                            }}
                        />
                        <TableBody 
                            parentTable={targetTable}
                            processedData={processedData}
                            allColumns={tableData.columns}
                            columns={visibleColumns}
                            pkCols={tableData.getPrimaryKeyColumns()}
                            fks={tableData.fks}
                            reverseFks={tableData.reverseFks}
                            fkOptions={tableData.fkOptions}
                            isPolymorphicTable={tableData.isPolymorphicTable}
                            polymorphicLabels={tableData.polymorphicLabels}
                            editingRowPk={editingRowPk}
                            handleEditClick={handleEditClick}
                            handleDeleteClick={handleDeleteClick}
                            handleSaveClick={handleSaveClick}
                            handleCancelClick={() => { setIsAdding(false); setEditingRowPk(null); setEditFormData({}); }}
                            schemaWarning={tableData.schemaWarning}
                            isAdding={isAdding}
                            editFormData={editFormData}
                            handleInputChange={handleInputChange}
                            uniqueSchemas={uniqueSchemas}
                            tablesList={tableData.tablesList}
                            loadingPolyRecords={loadingPolyRecords}
                            polymorphicRecords={polymorphicRecords}
                            distinctMode={tableState.distinctMode}
                            selectionMode={selectionMode}
                            onSelectRecord={onSelectRecord}
                            SubgridComponent={DynamicTableViewer}
                            forceHideHeaders={passCascadeHideHeaders}
                            activeSubgrids={tableState.activeSubgrids}
                            token={token}
                            uiConfig={uiConfig}
                            apiPrefix={apiPrefix}
                            draggingCol={tableState.draggingCol}
                            dragOverCol={tableState.dragOverCol}
                        />
                    </table>
                )}
            </div>

            {/* Deletion Modal */}
            {deletingRowPk !== null && (
                <div className="absolute inset-0 bg-slate-950/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold text-red-400 mb-4">Confirm Deletion</h2>
                        <p className="text-slate-300 mb-4">Are you sure you want to delete record ID <strong>{deletingRowPk}</strong>?</p>

                        {tableData.deletingLoading ? (
                            <div className="bg-slate-900 rounded p-4 text-center text-slate-400 mb-4 border border-slate-700">Analyzing dependencies...</div>
                        ) : tableData.dependencyGraph && tableData.dependencyGraph.length > 0 ? (
                            <div className="bg-red-900/30 border border-red-800 rounded p-4 mb-4">
                                <h4 className="text-red-300 font-bold mb-2">Dependency Warning</h4>
                                <ul className="text-sm text-red-300 space-y-1">
                                    {tableData.dependencyGraph.map((dep, i) => (
                                        <li key={i}>• <strong>{dep.count}</strong> record(s) in <code>{dep.table}</code></li>
                                    ))}
                                </ul>
                            </div>
                        ) : tableData.dependencyGraph && tableData.dependencyGraph.length === 0 ? (
                            <div className="bg-green-900/30 border border-green-800 rounded p-4 mb-4 text-green-300 text-sm">✓ Safe to delete.</div>
                        ) : null}

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setDeletingRowPk(null)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded">Cancel</button>
                            <button onClick={confirmDelete} disabled={tableData.deletingLoading} className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded font-bold">Force Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Record Modal */}
            {isAdding && (
                <AddRecordModal
                    targetTable={targetTable}
                    columns={tableData.columns}
                    fks={tableData.fks}
                    isPolymorphicTable={tableData.isPolymorphicTable}
                    editFormData={editFormData}
                    handleInputChange={handleInputChange}
                    handleSaveClick={handleSaveClick}
                    onCancel={() => { setIsAdding(false); setEditFormData({}); }}
                    token={token}
                />
            )}
        </div>
    );
}

function CollapsibleGroup({
    groupValue, groupColumn, rows, depth, effectiveGroupByConfig, tableData, tableState,
    visibleColumns, targetTable, token, uiConfig, apiPrefix, selectionMode, onSelectRecord,
    isHeadersHidden, passCascadeHideHeaders, editingRowPk, handleEditClick, handleDeleteClick,
    handleSaveClick, handleCancelClick, isAdding, editFormData, handleInputChange,
    uniqueSchemas, loadingPolyRecords, polymorphicRecords
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const groups = useMemo(() => {
        if (!effectiveGroupByConfig || effectiveGroupByConfig.length <= depth + 1) return null;
        
        const currentKeyFull = effectiveGroupByConfig[depth + 1];
        const [currentKey, depthStr] = currentKeyFull.split('::');
        const hierarchyDepth = depthStr ? parseInt(depthStr, 10) : null;
        
        const grouped = {};
        const standaloneRows = [];
        
        rows.forEach(row => {
            let groupVal = row[currentKey];
            
            if (tableData.isPolymorphicTable && currentKey === 'TargetRecordID') {
                const polyKey = `${row.TargetSchema}.${row.TargetTable}.${row.TargetRecordID}`;
                if (tableData.polymorphicLabels && tableData.polymorphicLabels[polyKey]) {
                    groupVal = tableData.polymorphicLabels[polyKey];
                }
            }
            
            if (hierarchyDepth !== null && typeof groupVal === 'string') {
                const parts = groupVal.split(' \\ ');
                if (hierarchyDepth < parts.length) {
                    groupVal = parts[hierarchyDepth];
                } else {
                    standaloneRows.push(row);
                    return;
                }
            }
            
            const keyStr = String(groupVal ?? 'null');
            if (!grouped[keyStr]) grouped[keyStr] = [];
            grouped[keyStr].push(row);
        });
        return { groupCol: currentKeyFull, chunks: grouped, standaloneRows };
    }, [rows, effectiveGroupByConfig, tableData.isPolymorphicTable, tableData.polymorphicLabels, depth]);

    return (
        <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-sm flex flex-col group/cg">
            <div 
                className="flex items-center justify-between p-2 bg-slate-800 text-slate-300 cursor-pointer hover:bg-slate-700 transition-colors border-b border-slate-700/50 rounded-t-lg"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-1">
                    <span className={`material-symbols-outlined text-[20px] transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                        chevron_right
                    </span>
                    <span className="text-white text-lg font-bold">
                        {groupValue === 'null' ? '(empty)' : groupValue}
                    </span>
                    <span className="text-slate-400 font-normal ml-2">
                        ({rows.length} records)
                    </span>
                </div>
            </div>
            
            {isExpanded && (
                <div className="bg-slate-950 p-2 overflow-x-auto rounded-b-lg">
                    {groups ? (
                        <div className="flex flex-col gap-2">
                            {Object.entries(groups.chunks).map(([subVal, subRows]) => (
                                <CollapsibleGroup
                                    key={subVal}
                                    groupValue={subVal}
                                    groupColumn={groups.groupCol}
                                    rows={subRows}
                                    depth={depth + 1}
                                    effectiveGroupByConfig={effectiveGroupByConfig}
                                    tableData={tableData}
                                    tableState={tableState}
                                    visibleColumns={visibleColumns}
                                    targetTable={targetTable}
                                    token={token}
                                    uiConfig={uiConfig}
                                    apiPrefix={apiPrefix}
                                    selectionMode={selectionMode}
                                    onSelectRecord={onSelectRecord}
                                    isHeadersHidden={isHeadersHidden}
                                    passCascadeHideHeaders={passCascadeHideHeaders}
                                    editingRowPk={editingRowPk}
                                    handleEditClick={handleEditClick}
                                    handleDeleteClick={handleDeleteClick}
                                    handleSaveClick={handleSaveClick}
                                    handleCancelClick={handleCancelClick}
                                    isAdding={isAdding}
                                    editFormData={editFormData}
                                    handleInputChange={handleInputChange}
                                    uniqueSchemas={uniqueSchemas}
                                    loadingPolyRecords={loadingPolyRecords}
                                    polymorphicRecords={polymorphicRecords}
                                />
                            ))}
                            {groups.standaloneRows && groups.standaloneRows.length > 0 && (
                                <table className="w-full text-left text-sm text-slate-300 mt-2">
                                    <TableHeaders 
                                        allColumns={tableData.columns}
                                        columns={visibleColumns}
                                        fks={tableData.fks}
                                        fkOptions={tableData.fkOptions}
                                        sortConfig={tableState.sortConfig}
                                        handleSort={tableState.handleSort}
                                        isHeadersHidden={isHeadersHidden}
                                        headerVisibility={tableState.headerVisibility}
                                        setHeaderVisibility={tableState.setHeaderVisibility}
                                        cascadeHideHeaders={tableState.cascadeHideHeaders}
                                        setCascadeHideHeaders={tableState.setCascadeHideHeaders}
                                        columnWidths={tableState.columnWidths}
                                        setColumnWidths={tableState.setColumnWidths}
                                        customColumnNames={tableState.customColumnNames}
                                        setCustomColumnNames={tableState.setCustomColumnNames}
                                        columnFilters={tableState.columnFilters}
                                        setColumnFilters={tableState.setColumnFilters}
                                        setHiddenColumns={tableState.setHiddenColumns}
                                        toggleInjectedColumn={tableState.toggleInjectedColumn}
                                        draggingCol={tableState.draggingCol}
                                        setDraggingCol={tableState.setDraggingCol}
                                        dragOverCol={tableState.dragOverCol}
                                        setDragOverCol={tableState.setDragOverCol}
                                        addSelfSubgrid={() => {
                                            const sg = `${targetTable}:SELF`;
                                            tableState.setActiveSubgrids(prev => prev.includes(sg) ? prev : [...prev, sg]);
                                        }}
                                    />
                                <TableBody 
                                        parentTable={targetTable}
                                        processedData={groups.standaloneRows}
                                        allColumns={tableData.columns}
                                        columns={visibleColumns}
                                        pkCols={tableData.getPrimaryKeyColumns()}
                                        fks={tableData.fks}
                                        reverseFks={tableData.reverseFks}
                                        fkOptions={tableData.fkOptions}
                                        isPolymorphicTable={tableData.isPolymorphicTable}
                                        polymorphicLabels={tableData.polymorphicLabels}
                                        editingRowPk={editingRowPk}
                                        handleEditClick={handleEditClick}
                                        handleDeleteClick={handleDeleteClick}
                                        handleSaveClick={handleSaveClick}
                                        handleCancelClick={handleCancelClick}
                                        schemaWarning={tableData.schemaWarning}
                                        isAdding={isAdding}
                                        editFormData={editFormData}
                                        handleInputChange={handleInputChange}
                                        uniqueSchemas={uniqueSchemas}
                                        tablesList={tableData.tablesList}
                                        loadingPolyRecords={loadingPolyRecords}
                                        polymorphicRecords={polymorphicRecords}
                                        distinctMode={tableState.distinctMode}
                                        selectionMode={selectionMode}
                                        onSelectRecord={onSelectRecord}
                                        SubgridComponent={DynamicTableViewer}
                                        forceHideHeaders={passCascadeHideHeaders}
                                        activeSubgrids={tableState.activeSubgrids}
                                        token={token}
                                        uiConfig={uiConfig}
                                        apiPrefix={apiPrefix}
                                        draggingCol={tableState.draggingCol}
                                        dragOverCol={tableState.dragOverCol}
                                    />
                                </table>
                            )}
                        </div>
                    ) : (
                        <table className="w-full text-left text-sm text-slate-300">
                            <TableHeaders 
                                allColumns={tableData.columns}
                                columns={visibleColumns}
                                fks={tableData.fks}
                                fkOptions={tableData.fkOptions}
                                sortConfig={tableState.sortConfig}
                                handleSort={tableState.handleSort}
                                isHeadersHidden={isHeadersHidden}
                                headerVisibility={tableState.headerVisibility}
                                setHeaderVisibility={tableState.setHeaderVisibility}
                                cascadeHideHeaders={tableState.cascadeHideHeaders}
                                setCascadeHideHeaders={tableState.setCascadeHideHeaders}
                                columnWidths={tableState.columnWidths}
                                setColumnWidths={tableState.setColumnWidths}
                                customColumnNames={tableState.customColumnNames}
                                setCustomColumnNames={tableState.setCustomColumnNames}
                                columnFilters={tableState.columnFilters}
                                setColumnFilters={tableState.setColumnFilters}
                                setHiddenColumns={tableState.setHiddenColumns}
                                toggleInjectedColumn={tableState.toggleInjectedColumn}
                                draggingCol={tableState.draggingCol}
                                setDraggingCol={tableState.setDraggingCol}
                                dragOverCol={tableState.dragOverCol}
                                setDragOverCol={tableState.setDragOverCol}
                                addSelfSubgrid={() => {
                                    const sg = `${targetTable}:SELF`;
                                    tableState.setActiveSubgrids(prev => prev.includes(sg) ? prev : [...prev, sg]);
                                }}
                            />
                            <TableBody 
                                parentTable={targetTable}
                                processedData={rows}
                                allColumns={tableData.columns}
                                columns={visibleColumns}
                                pkCols={tableData.getPrimaryKeyColumns()}
                                fks={tableData.fks}
                                reverseFks={tableData.reverseFks}
                                fkOptions={tableData.fkOptions}
                                isPolymorphicTable={tableData.isPolymorphicTable}
                                polymorphicLabels={tableData.polymorphicLabels}
                                editingRowPk={editingRowPk}
                                handleEditClick={handleEditClick}
                                handleDeleteClick={handleDeleteClick}
                                handleSaveClick={handleSaveClick}
                                handleCancelClick={handleCancelClick}
                                schemaWarning={tableData.schemaWarning}
                                isAdding={isAdding}
                                editFormData={editFormData}
                                handleInputChange={handleInputChange}
                                uniqueSchemas={uniqueSchemas}
                                tablesList={tableData.tablesList}
                                loadingPolyRecords={loadingPolyRecords}
                                polymorphicRecords={polymorphicRecords}
                                distinctMode={tableState.distinctMode}
                                selectionMode={selectionMode}
                                onSelectRecord={onSelectRecord}
                                SubgridComponent={DynamicTableViewer}
                                forceHideHeaders={passCascadeHideHeaders}
                                activeSubgrids={tableState.activeSubgrids}
                                token={token}
                                uiConfig={uiConfig}
                                apiPrefix={apiPrefix}
                            />
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}
